import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";
import { API_ENDPOINTS, METHOD_TYPE } from "../../utils/apiUrls";
import api from "../../utils/api";

const initialState = {
  isLoading: false,
  isButtonLoader: false,
  isButtonLoader2: false,
  error: null,
  entityClientCompany: [],
  entityClientBank: [],
  personalDetails: {},
  firstIdentityRetryAttempt: 3,
  secondIdentityRetryAttempt: 3,
  autoSearchAddressData: [],
  autoPrevSearchAddressData: [],
  getIndividualProfileByOomeroIdData: null,
  individualOnboardUsingOomeroIdData: [],
  getCorporateProfileByOomeroIdData: null,
  corporateOnboardingUsingOomeroIdData: []
}

export const saveIndividualPersonalDetails = createAsyncThunk("individual/saveIndividualPersonalDetails", async (requestData) => {
  try {
    let data = {
      method: METHOD_TYPE.post,
      url: API_ENDPOINTS.saveIndividualPersonalDetails,
      data: requestData.requestBody,
      headers: {}
    };
    if (requestData?.webformToken) {
      data['headers']['publictoken'] = requestData?.webformToken
    }
    const response = await api(data);
    return response.data;

  } catch (error) {
    throw error?.response?.data;
  }
});

export const uploadEntityClientDocument = createAsyncThunk("individual/uploadEntityClientDocument", async (requestData) => {
  try {
    let data = {
      method: METHOD_TYPE.post,
      url: API_ENDPOINTS.uploadEntityClientDocument,
      data: requestData.formData,
      headers: {
        'Content-Type': 'multipart/form-data', // Set the appropriate content type for file uploads
      },
    };
    if (requestData?.webformToken) {
      data.headers['publictoken'] = requestData?.webformToken
    }
    const response = await api(data);
    return response.data;

  } catch (error) {
    throw error?.response?.data;
  }
});

export const updateIndividualPersonalDetails = createAsyncThunk("individual/updateIndividualPersonalDetails", async (request, { rejectWithValue }) => {
  // An update needs a real client id. Without one the API only saw the text "undefined"
  // and answered "Entity Client Id must be a number", which told nobody what was wrong.
  if (request?.entityClientId === undefined || request?.entityClientId === null || request?.entityClientId === '') {
    return rejectWithValue({ message: 'This application has no client record yet, so it cannot be updated. Please try saving again.' });
  }
  try {
    let data = {
      method: METHOD_TYPE.put,
      url: API_ENDPOINTS.updateIndividualPersonalDetails + '?entity_client_id=' + request.entityClientId,
      data: request.requestBody,
      headers: {},
    };
    if (request?.webformToken) {
      data.headers['publictoken'] = request.webformToken
    }
    const response = await api(data);
    return response.data;

  } catch (error) {
    return rejectWithValue(error?.response?.data || { message: error?.message || 'Unable to update personal details.' });
  }
});

export const saveIndividualAddress = createAsyncThunk("individual/saveIndividualAddress", async (requestData) => {
  try {
    let data = {
      method: METHOD_TYPE.post,
      url: API_ENDPOINTS.saveIndividualAddress + '?entity_client_id=' + requestData.entityClientId,
      data: requestData.requestBody,
      headers: {}
    };
    if (requestData?.webformToken) {
      data.headers['publictoken'] = requestData.webformToken;
    }
    const response = await api(data);
    return response.data;

  } catch (error) {
    throw error?.response?.data;
  }
});

export const saveEntityClientKycDetail = createAsyncThunk("individual/saveEntityClientKycDetail", async (requestData) => {
  try {
    const params = new URLSearchParams();
    params.append('entity_client_id', requestData.entityClientId);
    if (typeof requestData.rekyc !== 'undefined') {
      params.append('rekyc', requestData.rekyc);
    }
    if (requestData.kyc_type) {
      params.append('kyc_type', requestData.kyc_type);
    }

    let data = {
      method: METHOD_TYPE.post,
      url: `${API_ENDPOINTS.saveEntityClientKycDetail}?${params.toString()}`,
      data: requestData.requestData,
      headers: {}
    };
    if (requestData?.webformToken) {
      data.headers['publictoken'] = requestData.webformToken;
    }
    const response = await api(data);
    return response.data;

  } catch (error) {
    throw error?.response?.data;
  }
});

export const fetchDataEntityClientCompany = createAsyncThunk("individual_onboarding/getEntityClientCompany", async (requestData) => {
  try {
    const data = {
      method: METHOD_TYPE.get,
      url: `${API_ENDPOINTS.getEntityClientCompany}${requestData.queryParam}`,
      headers: {},
    };
    if (requestData?.webformToken) {
      data.headers['publictoken'] = requestData.webformToken;
    }
    const response = await api(data);
    return response.data;
  } catch (error) {
    throw error?.response?.data;
  }
});

export const fetchDataEntityClientBank = createAsyncThunk("individual_onboarding/getEntityClientBank", async (requestData) => {
  try {
    const data = {
      method: METHOD_TYPE.get,
      url: `${API_ENDPOINTS.getEntityClientBank}${requestData.queryParam}`,
      headers: {},
    };
    if (requestData?.webformToken) {
      data.headers['publictoken'] = requestData.webformToken;
    }
    const response = await api(data);
    return response.data;
  } catch (error) {

    throw error?.response?.data;
  }
});

export const saveEntityClientCompany = createAsyncThunk("individual_onboarding/postEntityClientCompany", async ({ queryParam, requestData, webformToken }) => {
  try {
    const data = {
      method: METHOD_TYPE.post,
      url: `${API_ENDPOINTS.postEntityClientCompany}${queryParam}`,
      data: requestData,
      headers: {}
    };
    if (webformToken) {
      data['headers']['publictoken'] = webformToken
    }
    const response = await api(data);
    return response.data;
  } catch (error) {

    throw error?.response?.data;
  }
});

export const saveEntityClientBank = createAsyncThunk("individual_onboarding/createEntityClientBanks", async ({ queryParam, requestData, webformToken }) => {
  try {
    const data = {
      method: METHOD_TYPE.post,
      url: `${API_ENDPOINTS.postEntityClientBank}${queryParam}`,
      data: requestData,
      headers: {}
    };
    if (webformToken) {
      data['headers']['publictoken'] = webformToken
    }
    const response = await api(data);
    return response.data;
  } catch (error) {
    throw error?.response?.data;
  }
});

export const generateOnboardingInviteLink = createAsyncThunk("individual_onboarding/generateOnboardingInviteLink", async (requestData) => {
  try {
    const data = {
      method: METHOD_TYPE.post,
      url: `${API_ENDPOINTS.generateOnboardingInviteLink}`,
      data: requestData,
    };
    const response = await api(data);
    return response.data;
  } catch (error) {
    throw error?.response?.data;
  }
});

export const fetchEntityClientPersonalDetail = createAsyncThunk("individual_onboarding/fetchEntityClientPersonalDetail", async (requestData, { rejectWithValue }) => {
  try {
    const data = {
      method: METHOD_TYPE.get,
      url: `${API_ENDPOINTS.fetchEntityClientPersonalDetail}${requestData.queryParam}`,
      headers: {}
    };
    if (requestData?.webformToken) {
      data['headers']['publictoken'] = requestData?.webformToken
    }
    const response = await api(data);
    return response.data;
  } catch (error) {
    // Callers decide "new record" from a 404. Re-throwing the body lost the HTTP status
    // (Redux Toolkit keeps only name/message/stack of a thrown value), so a customer's
    // first save was sent as an update to "?entity_client_id=undefined".
    const body = error?.response?.data;
    return rejectWithValue({
      ...(body && typeof body === 'object' ? body : {}),
      status: error?.response?.status ?? null,
      message: body?.message || error?.message || 'Unable to load personal details.',
    });
  }
});

export const fetchAmlCheckSearchDetails = createAsyncThunk("individual_onboarding/fetchAmlCheckSearchDetails", async (queryParam) => {
  try {
    const data = {
      method: METHOD_TYPE.get,
      url: `${API_ENDPOINTS.fetchAmlCheckSearchDetails}${queryParam}`,
      headers: {}
    };
    const response = await api(data);
    return response.data;
  } catch (error) {
    throw error?.response?.data;
  }
});

export const fetchEntityClientAddress = createAsyncThunk("individual_onboarding/fetchEntityClientAddress", async (requestData) => {
  try {
    const data = {
      method: METHOD_TYPE.get,
      url: `${API_ENDPOINTS.fetchEntityClientAddress}${requestData.queryParam}`,
      headers: {}
    };
    if (requestData?.webformToken) {
      data['headers']['publictoken'] = requestData?.webformToken
    }
    const response = await api(data);
    return response.data;
  } catch (error) {

    throw error?.response?.data;
  }
});

export const fetchEntityData = createAsyncThunk("entity/fetchEntityData", async (requestData) => {
  try {
    let data = {
      method: METHOD_TYPE.get,
      url: API_ENDPOINTS.getEntityDetails + requestData.queryParams,
      headers: {}
    };
    if (requestData?.webformToken) {
      data['headers']['publictoken'] = requestData?.webformToken
    }
    const response = await api(data);
    return response.data;

  } catch (error) {
    throw error?.response?.data;
  }
});

export const deductKYCLivelynessCharge = createAsyncThunk("deductKYCLivelynessCharge", async ({ requestData, webformToken }) => {
  try {
    let data = {
      method: METHOD_TYPE.post,
      url: API_ENDPOINTS.deductKYCLivelynessCharge,
      headers: {},
      data: requestData
    };
    if (webformToken) {
      data['headers']['publictoken'] = webformToken
    }
    const response = await api(data);
    return response.data;

  } catch (error) {
    throw error?.response?.data;
  }
});

export const checkUKVatNo = createAsyncThunk("checkUKVatNo", async ({ queryParam, webformToken }) => {
  try {
    let data = {
      method: METHOD_TYPE.get,
      url: API_ENDPOINTS.checkUKVatNo + queryParam,
      headers: {}
    };
    if (webformToken) {
      data['headers']['publictoken'] = webformToken
    }
    const response = await api(data);
    return response.data;

  } catch (error) {
    throw error?.response?.data;
  }
});

export const autoSearchAddress = createAsyncThunk("autoSearchAddress", async (requestData) => {
  try {
    let data = {
      method: METHOD_TYPE.get,
      url: API_ENDPOINTS.autoSearchAddress + requestData?.queryParam,
      headers: {}
    };
    if (requestData?.webformToken) {
      data['headers']['publictoken'] = requestData?.webformToken
    }
    const response = await api(data);
    return response.data;

  } catch (error) {
    throw error?.response?.data;
  }
});

export const autoPrevSearchAddress = createAsyncThunk("autoPrevSearchAddress", async (requestData) => {
  try {
    let data = {
      method: METHOD_TYPE.get,
      url: API_ENDPOINTS.autoSearchAddress + requestData?.queryParam,
      headers: {}
    };
    if (requestData?.webformToken) {
      data['headers']['publictoken'] = requestData?.webformToken
    }
    const response = await api(data);
    return response.data;

  } catch (error) {
    throw error?.response?.data;
  }
});

export const checkEUVatNo = createAsyncThunk("checkEUVatNo", async ({ queryParam, webformToken }) => {
  try {
    let data = {
      method: METHOD_TYPE.get,
      url: API_ENDPOINTS.checkEUVatNo + queryParam,
      headers: {}
    };
    if (webformToken) {
      data['headers']['publictoken'] = webformToken
    }
    const response = await api(data);
    return response.data;

  } catch (error) {
    throw error?.response?.data;
  }
});
export const checkAadhaar = createAsyncThunk("checkAadhaar", async ({ requestData, webformToken }) => {
  try {
    let data = {
      method: METHOD_TYPE.post,
      url: API_ENDPOINTS.checkAadhaarUrl,
      data: requestData,
      headers: {}
    };
    if (webformToken) {
      data['headers']['publictoken'] = webformToken
    }
    const response = await api(data);
    return response.data;

  } catch (error) {
    throw error?.response?.data;
  }
});
export const checkPan = createAsyncThunk("checkPan", async ({ requestData, webformToken }) => {
  try {
    let data = {
      method: METHOD_TYPE.post,
      url: API_ENDPOINTS.checkPanUrl,
      data: requestData,
      headers: {}
    };
    if (webformToken) {
      data['headers']['publictoken'] = webformToken
    }
    const response = await api(data);
    return response.data;

  } catch (error) {
    throw error?.response?.data;
  }
});
export const saveCorporateClientOfficer = createAsyncThunk("saveCorporateClientOfficer", async ({ requestData, webformToken }) => {
  try {
    let data = {
      method: METHOD_TYPE.post,
      url: API_ENDPOINTS.saveCorporateClientOfficer,
      data: requestData,
      headers: {}
    };
    if (webformToken) {
      data['headers']['publictoken'] = webformToken
    }
    const response = await api(data);
    return response.data;

  } catch (error) {
    throw error?.response?.data || error;
  }
});
export const updateCorporateClientOfficer = createAsyncThunk("updateCorporateClientOfficer", async ({ requestData, webformToken }) => {
  try {
    let data = {
      method: METHOD_TYPE.post,
      url: API_ENDPOINTS.updateCorporateClientOfficer,
      data: requestData,
      headers: {}
    };
    if (webformToken) {
      data['headers']['publictoken'] = webformToken
    }
    const response = await api(data);
    return response.data;

  } catch (error) {
    throw error?.response?.data || error;
  }
});
export const triggerKycOnboardEmail = createAsyncThunk("triggerKycOnboardEmail", async ({ requestData, webformToken }) => {
  try {
    let data = {
      method: METHOD_TYPE.post,
      url: API_ENDPOINTS.triggerKycOnboardEmail,
      data: requestData,
      headers: {}
    };
    if (webformToken) {
      data['headers']['publictoken'] = webformToken
    }
    const response = await api(data);
    return response.data;

  } catch (error) {
    throw error?.response?.data || error;
  }
});
export const saveCorporateIndividualShareholder = createAsyncThunk("saveCorporateIndividualShareholder", async ({ requestData, webformToken }) => {
  try {
    let data = {
      method: METHOD_TYPE.post,
      url: API_ENDPOINTS.saveCorporateIndividualShareholder,
      data: requestData,
      headers: {}
    };
    if (webformToken) {
      data['headers']['publictoken'] = webformToken
    }
    const response = await api(data);
    return response.data;

  } catch (error) {
    throw error?.response?.data || error;
  }
});
export const updateCorporateIndividualShareholder = createAsyncThunk("updateCorporateIndividualShareholder", async ({ requestData, webformToken }) => {
  try {
    let data = {
      method: METHOD_TYPE.post,
      url: API_ENDPOINTS.updateCorporateIndividualShareholder,
      data: requestData,
      headers: {}
    };
    if (webformToken) {
      data['headers']['publictoken'] = webformToken
    }
    const response = await api(data);
    return response.data;

  } catch (error) {
    throw error?.response?.data || error;
  }
});

export const getIndividualProfileByOomeroId = createAsyncThunk("getIndividualProfileByOomeroId", async (queryParams) => {
  try {
    let data = {
      method: METHOD_TYPE.get,
      url: API_ENDPOINTS.getIndividualProfileByOomeroId + queryParams
    };
    // if (webformToken) {
    //   data['headers']['publictoken'] = webformToken
    // }
    const response = await api(data);
    return response.data;
  } catch (error) {
    throw error?.response?.data;
  }
});

export const individualOnboardUsingOomeroId = createAsyncThunk("individualOnboardUsingOomeroId", async ({ queryParams, dataPayload }) => {
  try {
    let data = {
      method: METHOD_TYPE.post,
      url: API_ENDPOINTS.individualOnboardUsingOomeroId + queryParams,
      data: dataPayload
    };
    // if (webformToken) {
    //   data['headers']['publictoken'] = webformToken
    // }
    const response = await api(data);
    return response.data;
  } catch (error) {
    throw error?.response?.data;
  }
});

export const getCorporateProfileByOomeroId = createAsyncThunk("getCorporateProfileByOomeroId", async (queryParams) => {
  try {
    let data = {
      method: METHOD_TYPE.get,
      url: API_ENDPOINTS.getCorporateProfileByOomeroId + queryParams
    };
    // if (webformToken) {
    //   data['headers']['publictoken'] = webformToken
    // }
    const response = await api(data);
    return response.data;
  } catch (error) {
    throw error?.response?.data;
  }
});

export const corporateOnboardingUsingOomeroId = createAsyncThunk("corporateOnboardingUsingOomeroId", async ({ queryParams, dataPayload }) => {
  try {
    let data = {
      method: METHOD_TYPE.post,
      url: API_ENDPOINTS.corporateOnboardingUsingOomeroId + queryParams,
      data: dataPayload
    };
    // if (webformToken) {
    //   data['headers']['publictoken'] = webformToken
    // }
    const response = await api(data);
    return response.data;
  } catch (error) {
    throw error?.response?.data;
  }
});

export const generateFaceKycLink = createAsyncThunk("individual_onboarding/generateFaceKycLink", async (requestData) => {
  try {
    const data = {
      method: METHOD_TYPE.post,
      url: `${API_ENDPOINTS.generateFaceKycLink}`,
      data: requestData,
    };
    const response = await api(data);
    return response.data;
  } catch (error) {
    throw error?.response?.data;
  }
});

export const generateFaciaKycUrl = createAsyncThunk("individual_onboarding/generateFaciaKycUrl", async (requestData) => {
  try {
    const data = {
      method: METHOD_TYPE.post,
      url: `/individual_onboarding/generateFaciaKycUrl?entity_client_id=${requestData.entity_client_id}`,
      data: requestData.payload,
      headers: { ...(requestData.webformToken && { 'publictoken': requestData.webformToken }) }
    };
    const response = await api(data);
    return response.data;
  } catch (error) {
    throw error?.response?.data;
  }
});

export const getFaciaKycStatus = createAsyncThunk("individual_onboarding/getFaciaKycStatus", async (requestData) => {
  try {
    const data = {
      method: METHOD_TYPE.get,
      url: `/individual_onboarding/getFaciaKycStatus?entity_client_id=${requestData.entity_client_id}`,
      headers: { ...(requestData.webformToken && { 'publictoken': requestData.webformToken }) }
    };
    const response = await api(data);
    return response.data;
  } catch (error) {
    throw error?.response?.data;
  }
});

// Called when user completes Facia KYC — triggers backend to store result in entity_client_facia_kyc table
// and update entity_client.facia_kyc_status so getFaciaKycStatus can return the correct value.
export const triggerFaciaCallback = createAsyncThunk("individual_onboarding/faciaCallback", async (requestData) => {
  try {
    const data = {
      method: METHOD_TYPE.post,
      url: `/individual_onboarding/faciaCallback`,
      data: requestData.payload,
      headers: { ...(requestData.webformToken && { 'publictoken': requestData.webformToken }) }
    };
    const response = await api(data);
    return response.data;
  } catch (error) {
    throw error?.response?.data;
  }
});

const profileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {
    adjustRetryCount(state, action) {
      const type = action.payload;
      if (type === 'firstIdentity') {
        if (state.firstIdentityRetryAttempt > 0) {
          state.firstIdentityRetryAttempt -= 1;
        }
      } else if (type === 'secondIdentity') {
        if (state.secondIdentityRetryAttempt > 0) {
          state.secondIdentityRetryAttempt -= 1;
        }
      }
    },
    resetRetryCount(state) {
      state.firstIdentityRetryAttempt = initialState.firstIdentityRetryAttempt;
      state.secondIdentityRetryAttempt = initialState.secondIdentityRetryAttempt;
    },
    resetAutoSearchAddress(state) {
      state.autoSearchAddressData = initialState.autoSearchAddressData;
    },
    resetPrevAutoSearchAddress(state) {
      state.autoPrevSearchAddressData = initialState.autoPrevSearchAddressData;
    },
    resetPersonalDetails(state) {
      state.personalDetails = initialState.personalDetails;
    },
    resetOnboardedWithOomeroIdData(state) {
      state.getIndividualProfileByOomeroIdData = initialState.getIndividualProfileByOomeroIdData;
      state.individualOnboardUsingOomeroIdData = initialState.individualOnboardUsingOomeroIdData;
      state.getCorporateProfileByOomeroIdData = initialState.getCorporateProfileByOomeroIdData;
      state.corporateOnboardingUsingOomeroIdData = initialState.corporateOnboardingUsingOomeroIdData;
    },
    resetOnboardId(state) {
      state.getIndividualProfileByOomeroIdData = initialState.getIndividualProfileByOomeroIdData;
      state.getCorporateProfileByOomeroIdData = initialState.getCorporateProfileByOomeroIdData;
      state.isButtonLoader = initialState.isButtonLoader;
      state.isButtonLoader2 = initialState.isButtonLoader2;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(saveIndividualPersonalDetails.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(triggerKycOnboardEmail.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(saveCorporateClientOfficer.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(updateCorporateClientOfficer.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(saveIndividualAddress.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(uploadEntityClientDocument.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(saveEntityClientKycDetail.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(saveEntityClientCompany.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(fetchDataEntityClientBank.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
        state.entityClientBank = payload.data;
      })
      .addCase(fetchDataEntityClientCompany.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
        state.entityClientCompany = payload.data;
      })
      .addCase(fetchEntityClientPersonalDetail.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
        state.personalDetails = payload.data;
      })
      .addCase(fetchAmlCheckSearchDetails.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
        state.personalDetails = payload.data;
      })
      .addCase(fetchEntityClientAddress.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(updateIndividualPersonalDetails.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(fetchEntityData.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(deductKYCLivelynessCharge.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(checkUKVatNo.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
      }).addCase(checkEUVatNo.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(generateOnboardingInviteLink.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(generateFaceKycLink.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(generateFaciaKycUrl.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(triggerFaciaCallback.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(autoSearchAddress.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.autoSearchAddressData = payload.data;
        state.error = null;
      })
      .addCase(autoPrevSearchAddress.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.autoPrevSearchAddressData = payload.data;
        state.error = null;
      })
      .addCase(checkAadhaar.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(checkPan.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(getIndividualProfileByOomeroId.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.getIndividualProfileByOomeroIdData = payload.data
        state.error = null;
      })
      .addCase(individualOnboardUsingOomeroId.fulfilled, (state, { payload }) => {
        state.isButtonLoader = false;
        state.individualOnboardUsingOomeroIdData = payload.data
        state.error = null;
      })
      .addCase(getCorporateProfileByOomeroId.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.getCorporateProfileByOomeroIdData = payload.data
        state.error = null;
      })
      .addCase(corporateOnboardingUsingOomeroId.fulfilled, (state, { payload }) => {
        state.isButtonLoader2 = false;
        state.corporateOnboardingUsingOomeroIdData = payload.data
        state.error = null;
      })
      .addMatcher(isAnyOf(saveIndividualPersonalDetails.pending, saveIndividualAddress.pending, uploadEntityClientDocument.pending, saveEntityClientBank.pending, fetchDataEntityClientBank.pending, fetchDataEntityClientBank.pending, saveEntityClientCompany.pending, saveEntityClientKycDetail.pending, fetchEntityClientPersonalDetail.pending, fetchEntityClientAddress.pending, updateIndividualPersonalDetails.pending, generateOnboardingInviteLink.pending, fetchEntityData.pending, deductKYCLivelynessCharge.pending, checkUKVatNo.pending, checkEUVatNo.pending, checkPan.pending, checkAadhaar.pending, fetchAmlCheckSearchDetails.pending, saveCorporateClientOfficer.pending, updateCorporateClientOfficer.pending, triggerKycOnboardEmail.pending, saveCorporateIndividualShareholder.pending, updateCorporateIndividualShareholder.pending, getIndividualProfileByOomeroId.pending, getCorporateProfileByOomeroId.pending, generateFaceKycLink.pending, generateFaciaKycUrl.pending, triggerFaciaCallback.pending), (state) => {
        state.isLoading = true;
      })
      .addMatcher(isAnyOf(individualOnboardUsingOomeroId.pending), (state) => {
        state.isButtonLoader = true;
      })
      .addMatcher(isAnyOf(corporateOnboardingUsingOomeroId.pending), (state) => {
        state.isButtonLoader2 = true;
      })
      .addMatcher(isAnyOf(saveIndividualPersonalDetails.rejected, saveIndividualAddress.rejected, uploadEntityClientDocument.rejected, fetchDataEntityClientBank.rejected, fetchDataEntityClientCompany.rejected, saveEntityClientBank.rejected, saveEntityClientCompany.rejected, saveEntityClientKycDetail.rejected, fetchEntityClientPersonalDetail.rejected, fetchEntityClientAddress.rejected, updateIndividualPersonalDetails.rejected, generateOnboardingInviteLink.rejected, fetchEntityData.rejected, deductKYCLivelynessCharge.rejected, checkUKVatNo.rejected, checkEUVatNo.rejected, checkPan.rejected, checkAadhaar.rejected, fetchAmlCheckSearchDetails.rejected, saveCorporateClientOfficer.rejected, triggerKycOnboardEmail.rejected, saveCorporateIndividualShareholder.rejected, updateCorporateClientOfficer.rejected, updateCorporateIndividualShareholder.rejected, getIndividualProfileByOomeroId.rejected, individualOnboardUsingOomeroId.rejected, getCorporateProfileByOomeroId.rejected, corporateOnboardingUsingOomeroId.rejected, generateFaceKycLink.rejected, generateFaciaKycUrl.rejected, triggerFaciaCallback.rejected), (state, { error }) => {
        state.isLoading = false;
        state.error = (error) ? error.message : "Request Failed Please Try Again ";
      })
  }
});

const { adjustRetryCount, resetRetryCount, resetAutoSearchAddress, resetPrevAutoSearchAddress, resetPersonalDetails, resetOnboardedWithOomeroIdData, resetOnboardId } = profileSlice.actions;
const profileReducer = profileSlice.reducer;

export { adjustRetryCount, resetRetryCount, profileReducer, resetAutoSearchAddress, resetPrevAutoSearchAddress, resetPersonalDetails, resetOnboardedWithOomeroIdData, resetOnboardId };
export default profileReducer;
